<script setup lang="ts">
import { computed, ref } from 'vue'
import ExpansionCard from '../components/ExpansionCard.vue'
import { exoticActivities, otherExotics, exoticActivitiesToWeapons } from '../releaseDetail'
import { ReleaseId } from '../enums/releases'
import { Expansions } from '../enums/expansions'
import { BeyondLightSeasonHashes, EdgeOfFateSeasonHashes, FinalShapeEpisodeHashes, LightfallSeasonHashes, RenegadesSeasonHashes, ShadowkeepSeasonHashes, WitchQueenSeasonHashes } from '../enums/seasons'
import { FinalShapeDungeonHashes, LightfallDungeonHashes, WitchQueenDungeonHashes } from '../enums/dungeons'

const emit = defineEmits(['showPastSeasons'])

const allExotics = [
  ...exoticActivitiesToWeapons(exoticActivities),
  ...otherExotics
]
  .sort((a, b) => a.name.localeCompare(b.name))
  .sort((a, b) => b.releasedWith - a.releasedWith)
  .sort((a, b) => a.date.getTime() - b.date.getTime())
const exoticOpen = ref<string>()
const filterRelease = ref<ReleaseId>(Expansions.Renegades)
const filteredExotics = computed(() => ({
  renegades: allExotics.filter(x => ([
    Expansions.Renegades,
    RenegadesSeasonHashes.Lawless,
    RenegadesSeasonHashes.MonumentOfTriumph,
  ] as ReleaseId[]).includes(x.releasedWith)),
  edgeOfFate: allExotics.filter(x => ([
    Expansions.EdgeOfFate,
    EdgeOfFateSeasonHashes.Reclamation,
    EdgeOfFateSeasonHashes.AshAndIron,
  ] as ReleaseId[]).includes(x.releasedWith)),
  finalShape: allExotics.filter(x => ([
    Expansions.FinalShape,
    FinalShapeEpisodeHashes.Echoes,
    FinalShapeEpisodeHashes.Revenant,
    FinalShapeEpisodeHashes.Heresy,
    FinalShapeEpisodeHashes.RiteOfTheNine,
    FinalShapeDungeonHashes.VespersHost,
    FinalShapeDungeonHashes.SunderedDoctrine
  ] as ReleaseId[]).includes(x.releasedWith)),
  lightfall: allExotics.filter(x => ([
    Expansions.Lightfall,
    LightfallSeasonHashes.Defiance,
    LightfallSeasonHashes.Deep,
    LightfallSeasonHashes.Witch,
    LightfallSeasonHashes.Wish,
    LightfallSeasonHashes.IntoTheLight,
    LightfallDungeonHashes.GhostsOfTheDeep,
    LightfallDungeonHashes.WarlordsRuin
  ] as ReleaseId[]).includes(x.releasedWith)),
  theWitchQueen: allExotics.filter(x => ([
    Expansions.TheWitchQueen,
    WitchQueenSeasonHashes.Risen,
    WitchQueenSeasonHashes.Haunted,
    WitchQueenSeasonHashes.Plunder,
    WitchQueenSeasonHashes.Seraph,
    WitchQueenDungeonHashes.Duality,
    WitchQueenDungeonHashes.SpireOfTheWatcher
  ] as ReleaseId[]).includes(x.releasedWith)),
  beyondLight: allExotics.filter(x => ([
    Expansions.BeyondLight,
    Expansions.Anniversary30th,
    BeyondLightSeasonHashes.Hunt,
    BeyondLightSeasonHashes.Chosen,
    BeyondLightSeasonHashes.Splicer,
    BeyondLightSeasonHashes.Lost,
  ] as ReleaseId[]).includes(x.releasedWith)),
  shadowkeep: allExotics.filter(x => ([
    Expansions.Shadowkeep,
    ShadowkeepSeasonHashes.Undying,
    ShadowkeepSeasonHashes.Dawn,
    ShadowkeepSeasonHashes.Worthy,
    ShadowkeepSeasonHashes.Arrivals
  ] as ReleaseId[]).includes(x.releasedWith)),
  forsaken: allExotics.filter(x => ([
    Expansions.Forsaken,
    Expansions.AnnualPass
  ] as ReleaseId[]).includes(x.releasedWith)),
  destiny2: allExotics.filter(x => ([
    Expansions.Destiny2,
    Expansions.CurseOfOsiris,
    Expansions.Warmind
  ] as ReleaseId[]).includes(x.releasedWith))
}))

function toggle(exoticId: string) {
  if (exoticOpen.value === exoticId) {
    exoticOpen.value = undefined
  } else {
    exoticOpen.value = exoticId
  }
}
</script>

<template>
  <div class="w-full max-w-[48rem] px-2 py-4 text-zinc-200 mx-auto">
    <h1 id="expansions" class="text-center text-4xl my-2 border-zinc-500 border-b pb-2">Exotics information</h1>
    <div class="w-full md:w-5/6 lg:w-4/5 px-2 py-4 text-zinc-200 mx-auto">
      <div class="flex justify-center items-center gap-1">
        <span class="leading-4 text-zinc-400 text-sm lg:whitespace-nowrap">Filter weapons:</span>
        <select v-model="filterRelease" class="lg:hidden bg-zinc-700 p-2">
          <option :value="Expansions.Renegades">Renegades</option>
          <option :value="Expansions.EdgeOfFate">Edge of Fate</option>
          <option :value="Expansions.FinalShape">Final Shape</option>
          <option :value="Expansions.Lightfall">Lightfall</option>
          <option :value="Expansions.TheWitchQueen">The Witch Queen</option>
          <option :value="Expansions.BeyondLight">Beyond Light</option>
          <option :value="Expansions.Shadowkeep">Shadowkeep</option>
          <option :value="Expansions.Forsaken">Forsaken</option>
          <option :value="Expansions.Destiny2">Destiny 2</option>
        </select>
        <!--
          max-w-[28rem] is set to fit exactly 3 buttons per row (each button is w-28 = 7rem, so 3 * 7rem = 21rem,
          plus gap-1 between buttons and some padding). If we add more buttons or change button width, update this value accordingly.
        -->
        <div class="justify-center items-start gap-1 hidden lg:flex flex-wrap max-w-[28rem]">
          <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterRelease === Expansions.Renegades, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterRelease !== Expansions.Renegades }" @click="() => filterRelease = Expansions.Renegades">RENEGADES</button>
          <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterRelease === Expansions.EdgeOfFate, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterRelease !== Expansions.EdgeOfFate }" @click="() => filterRelease = Expansions.EdgeOfFate">EDGE OF FATE</button>
          <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterRelease === Expansions.FinalShape, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterRelease !== Expansions.FinalShape }" @click="() => filterRelease = Expansions.FinalShape">FINAL SHAPE</button>
          
          <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterRelease === Expansions.Lightfall, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterRelease !== Expansions.Lightfall }" @click="() => filterRelease = Expansions.Lightfall">LIGHTFALL</button>
          <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterRelease === Expansions.TheWitchQueen, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterRelease !== Expansions.TheWitchQueen }" @click="() => filterRelease = Expansions.TheWitchQueen">THE WITCH QUEEN</button>
          <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterRelease === Expansions.BeyondLight, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterRelease !== Expansions.BeyondLight }" @click="() => filterRelease = Expansions.BeyondLight">BEYOND LIGHT</button>
          
          <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterRelease === Expansions.Shadowkeep, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterRelease !== Expansions.Shadowkeep }" @click="() => filterRelease = Expansions.Shadowkeep">SHADOWKEEP</button>
          <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterRelease === Expansions.Forsaken, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterRelease !== Expansions.Forsaken }" @click="() => filterRelease = Expansions.Forsaken">FORSAKEN</button>
          <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterRelease === Expansions.Destiny2, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterRelease !== Expansions.Destiny2 }" @click="() => filterRelease = Expansions.Destiny2">DESTINY 2</button>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap gap-4 justify-center text-black" v-show="filterRelease === Expansions.Renegades">
      <ExpansionCard
        v-for="exotic in filteredExotics.renegades"
        :key="exotic.name"
        :exotic="exotic"
        :open="exoticOpen === exotic.name"
        @toggle="() => toggle(exotic.name)"
      />
    </div>
    <div class="flex flex-wrap gap-4 justify-center text-black" v-show="filterRelease === Expansions.EdgeOfFate">
      <ExpansionCard
        v-for="exotic in filteredExotics.edgeOfFate"
        :key="exotic.name"
        :exotic="exotic"
        :open="exoticOpen === exotic.name"
        @toggle="() => toggle(exotic.name)"
      />
    </div>
    <div class="flex flex-wrap gap-4 justify-center text-black" v-show="filterRelease === Expansions.FinalShape">
      <ExpansionCard
        v-for="exotic in filteredExotics.finalShape"
        :key="exotic.name"
        :exotic="exotic"
        :open="exoticOpen === exotic.name"
        @toggle="() => toggle(exotic.name)"
      />
    </div>
    <div class="flex flex-wrap gap-4 justify-center text-black" v-show="filterRelease === Expansions.Lightfall">
      <ExpansionCard
        v-for="exotic in filteredExotics.lightfall"
        :key="exotic.name"
        :exotic="exotic"
        :open="exoticOpen === exotic.name"
        @toggle="() => toggle(exotic.name)"
      />
    </div>
    <div class="flex flex-wrap gap-4 justify-center text-black" v-show="filterRelease === Expansions.TheWitchQueen">
      <ExpansionCard
        v-for="exotic in filteredExotics.theWitchQueen"
        :key="exotic.name"
        :exotic="exotic"
        :open="exoticOpen === exotic.name"
        @toggle="() => toggle(exotic.name)"
      />
    </div>
    <div class="flex flex-wrap gap-4 justify-center text-black" v-show="filterRelease === Expansions.BeyondLight">
      <ExpansionCard
        v-for="exotic in filteredExotics.beyondLight"
        :key="exotic.name"
        :exotic="exotic"
        :open="exoticOpen === exotic.name"
        @toggle="() => toggle(exotic.name)"
      />
    </div>
    <div class="flex flex-wrap gap-4 justify-center text-black" v-show="filterRelease === Expansions.Shadowkeep">
      <ExpansionCard
        v-for="exotic in filteredExotics.shadowkeep"
        :key="exotic.name"
        :exotic="exotic"
        :open="exoticOpen === exotic.name"
        @toggle="() => toggle(exotic.name)"
      />
    </div>
    <div class="flex flex-wrap gap-4 justify-center text-black" v-show="filterRelease === Expansions.Forsaken">
      <ExpansionCard
        v-for="exotic in filteredExotics.forsaken"
        :key="exotic.name"
        :exotic="exotic"
        :open="exoticOpen === exotic.name"
        @toggle="() => toggle(exotic.name)"
      />
    </div>
    <div class="flex flex-wrap gap-4 justify-center text-black" v-show="filterRelease === Expansions.Destiny2">
      <ExpansionCard
        v-for="exotic in filteredExotics.destiny2"
        :key="exotic.name"
        :exotic="exotic"
        :open="exoticOpen === exotic.name"
        @toggle="() => toggle(exotic.name)"
      />
    </div>
  </div>
</template>